import { useState } from 'react';
import { Card, Button, Badge, Modal } from 'react-bootstrap';
import { FaDownload, FaFileAlt, FaFilePdf, FaFileImage, FaEye, FaIdCard, FaCreditCard, FaUniversity, FaUserCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const getFileIcon = (name = '', type = '') => {
  const ext = name.split('.').pop()?.toLowerCase();
  const mime = type?.toLowerCase() || '';
  if (mime.includes('pdf') || ext === 'pdf') return <FaFilePdf className="text-danger" style={{ fontSize: '1.5rem' }} />;
  if (mime.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
    return <FaFileImage className="text-primary" style={{ fontSize: '1.5rem' }} />;
  return <FaFileAlt className="text-secondary" style={{ fontSize: '1.5rem' }} />;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    const d = date?.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return 'N/A'; }
};

const DocumentsSection = ({ employee, onRefresh }) => {
  const { user } = useAuth();
  const isHROrAdmin = user?.role === 'hr' || user?.role === 'admin';

  const [previewUrl, setPreviewUrl]   = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const openPreview = (url, title) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
    setShowPreview(true);
  };

  // ── Build document list from all possible sources ────────────────────────────
  const buildDocuments = () => {
    const docs = [];

    // 1. Explicit documents[] array (tracked metadata)
    if (Array.isArray(employee?.documents)) {
      employee.documents.forEach((d, i) => {
        // Skip bank/aadhaar/pan docs — we'll add them separately below
        if (d.isBankDoc || d.isAadhaarDoc || d.isPanDoc) return;
        docs.push({
          key: `doc-${i}`,
          name: d.name || `Document ${i + 1}`,
          type: d.type || 'document',
          uploadedAt: d.uploadedAt,
          url: d.url || d.data || '',
          icon: <FaFileAlt className="text-secondary" style={{ fontSize: '1.8rem' }} />,
        });
      });
    }

    // 2. Aadhaar Card Scan (stored in personal.aadhaarScan as base64)
    if (employee?.personal?.aadhaarScan) {
      docs.push({
        key: 'aadhaar-scan',
        name: 'Aadhaar Card Scan',
        type: employee.personal.aadhaarScan.includes('pdf') ? 'application/pdf' : 'image',
        uploadedAt: null,
        url: employee.personal.aadhaarScan,
        icon: <FaIdCard className="text-orange" style={{ fontSize: '1.8rem', color: '#f97316' }} />,
        badge: 'Aadhaar',
        badgeColor: '#f97316',
      });
    }

    // 3. PAN Card Scan (stored in personal.panScan as base64)
    if (employee?.personal?.panScan) {
      docs.push({
        key: 'pan-scan',
        name: 'PAN Card Scan',
        type: employee.personal.panScan.includes('pdf') ? 'application/pdf' : 'image',
        uploadedAt: null,
        url: employee.personal.panScan,
        icon: <FaCreditCard className="text-info" style={{ fontSize: '1.8rem', color: '#06b6d4' }} />,
        badge: 'PAN Card',
        badgeColor: '#06b6d4',
      });
    }

    // 4. Bank Passbook / Cheque Scan (stored in bank.document as base64)
    if (employee?.bank?.document) {
      docs.push({
        key: 'bank-doc',
        name: 'Bank Passbook / Cheque Scan',
        type: employee.bank.document.includes('pdf') ? 'application/pdf' : 'image',
        uploadedAt: null,
        url: employee.bank.document,
        icon: <FaUniversity className="text-success" style={{ fontSize: '1.8rem', color: '#10b981' }} />,
        badge: 'Bank',
        badgeColor: '#10b981',
      });
    }

    // 5. Profile Photo
    if (employee?.personal?.photo) {
      docs.push({
        key: 'profile-photo',
        name: 'Profile Photo',
        type: 'image',
        uploadedAt: null,
        url: employee.personal.photo,
        icon: <FaUserCircle className="text-purple" style={{ fontSize: '1.8rem', color: '#8b5cf6' }} />,
        badge: 'Photo',
        badgeColor: '#8b5cf6',
      });
    }

    return docs;
  };

  const documents = buildDocuments();

  const handleDownload = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.target = '_blank';
    a.click();
  };

  return (
    <>
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom d-flex align-items-center justify-content-between py-3">
          <h6 className="mb-0 fw-bold">Documents</h6>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="secondary" pill>{documents.length} file{documents.length !== 1 ? 's' : ''}</Badge>
            {isHROrAdmin && (
              <Button size="sm" variant="primary" onClick={() => toast.info('Upload feature coming soon')}>
                + Upload
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {documents.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ marginBottom: '8px', opacity: 0.5 }}>
                <FaFileAlt size={36} color="#94a3b8" />
              </div>
              <p className="mb-1">No documents uploaded yet.</p>
              <small>Aadhaar, PAN, bank passbook and other documents will appear here once uploaded by HR.</small>
            </div>
          ) : (
            <div className="p-2 p-md-3">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: '10px' }}>
                {documents.map((doc) => (
                  <div
                    key={doc.key}
                    className="border rounded-3 p-2 p-md-3 d-flex align-items-center justify-content-between gap-2 gap-md-3"
                    style={{ background: '#fafafa', transition: 'all 0.2s', cursor: 'pointer' }}
                    onMouseEnter={e => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                      e.currentTarget.style.borderColor = '#667eea';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#dee2e6';
                    }}
                    onClick={() => openPreview(doc.url, doc.name)}
                    title={`Click to preview ${doc.name}`}
                  >
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
                      <div className="flex-shrink-0 d-flex align-items-center justify-content-center">{doc.icon}</div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="fw-bold text-truncate" style={{ fontSize: '0.85rem', color: '#1e293b' }} title={doc.name}>
                          {doc.name}
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          {doc.badge && (
                            <span style={{
                              fontSize: '0.62rem', fontWeight: 700, padding: '2px 8px',
                              borderRadius: '12px', color: '#fff',
                              background: doc.badgeColor || '#64748b'
                            }}>
                              {doc.badge}
                            </span>
                          )}
                          {doc.uploadedAt && (
                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                              {formatDate(doc.uploadedAt)}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {doc.url && (
                        <Button
                          size="sm"
                          variant="light"
                          className="text-primary border-0"
                          style={{ padding: '6px', borderRadius: '50%' }}
                          onClick={() => openPreview(doc.url, doc.name)}
                          title="View Document"
                        >
                          <FaEye size={15} />
                        </Button>
                      )}
                      {doc.url && (
                        <Button
                          size="sm"
                          variant="light"
                          className="text-secondary border-0"
                          style={{ padding: '6px', borderRadius: '50%' }}
                          onClick={() => handleDownload(doc.url, doc.name)}
                          title="Download Document"
                        >
                          <FaDownload size={15} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Document Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>{previewTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 text-center bg-light" style={{ minHeight: '60vh' }}>
          {previewUrl && (previewUrl.includes('pdf') || previewUrl.startsWith('data:application/pdf')) ? (
            <iframe
              src={previewUrl}
              title="Document Preview"
              style={{ width: '100%', height: '65vh', border: 'none' }}
            />
          ) : (
            <img
              src={previewUrl}
              alt="Document Preview"
              style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', padding: '16px' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowPreview(false)}>Close</Button>
          <a href={previewUrl} download={previewTitle} className="btn btn-primary btn-sm">
            <FaDownload className="me-1" /> Download
          </a>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DocumentsSection;
